import { useCallback, useLayoutEffect, useState, type PropsWithChildren } from 'react'

import { AxiosError, HttpStatusCode, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { t } from 'i18next'
import { toast } from 'sonner'

import type { TUnidadesDeNegocioDisponiveisResponse } from '@/features/regionais/interfaces/unidade-de-negocio.types'
import { useAuth } from '@/features/user/hooks'
import { useRefreshToken } from '@/features/user/hooks/use-refresh-token'
import { Toast } from '@/shared/components/ui/toast'
import { useLocalStorage } from '@/shared/hooks'
import type { TUsuarioLogado } from '@/shared/interfaces/user'
import { ventasApi } from '@/shared/lib'
import { showToast } from '@/shared/lib/sonner'

let isRefreshing = false
let refreshSubscribers: Array<() => void> = []

const subscribeTokenRefresh = (callback: () => void) => {
  refreshSubscribers.push(callback)
}

const onRefreshed = () => {
  for (const callback of refreshSubscribers) callback()
  refreshSubscribers = []
}

const toastSemInternet = () => {
  return toast.custom(
    () => (
      <Toast
        id="network_offline"
        dismiss={() => toast.dismiss('network_offline')}
        message={t('common:errors_gerais.sem_internet')}
        variant="error"
      />
    ),
    { id: 'network_offline' },
  )
}

const AxiosInterceptor = ({ api, children }: PropsWithChildren<{ api: AxiosInstance }>) => {
  const [isInicializado, setIsInicializado] = useState(false)
  const [user, setUser] = useLocalStorage<TUsuarioLogado | null>('user', null)
  const { mutateAsync: handleRefreshToken } = useRefreshToken()
  const { logout, setError } = useAuth()

  const handleTokenRefresh = (originalRequest: AxiosRequestConfig<unknown>) => {
    return new Promise((resolve) => {
      subscribeTokenRefresh(() => {
        resolve(ventasApi(originalRequest))
      })
    })
  }

  const processarMudancaUnidadeDeNegocio = useCallback(
    (unidadePrincipal: TUnidadesDeNegocioDisponiveisResponse['unidadesDisponiveis'][0]) => {
      setUser((prev) => {
        if (!prev) return null
        return {
          ...prev,
          unidadeDeNegocioId: unidadePrincipal.id,
          unidadeDeNegocio: unidadePrincipal.nome,
          empresaId: unidadePrincipal.empresaId,
        }
      })

      setTimeout(() => {
        showToast({ message: t('common:interceptor.regionais'), variant: 'error', closeButton: true })
      }, 100)
    },
    [setUser],
  )

  const recuperarUnidadeDeNegocio = useCallback(
    async (response: AxiosResponse) => {
      if (response.config.url !== 'api/v2/UnidadeDeNegocio/disponiveis') {
        return response
      }
      const regionais = response.data as TUnidadesDeNegocioDisponiveisResponse
      const unidadePrincipal = regionais.unidadesDisponiveis.find((unidade) => unidade.principal)

      if (!unidadePrincipal) {
        setError({ descricao: 'Unidade principal não encontrada', titulo: 'Erro' })
        await logout()
        throw new Error('Unidade principal não encontrada')
      }

      if (regionais?.unidadeDeNegocioSelecionadaIndisponivel) {
        processarMudancaUnidadeDeNegocio(unidadePrincipal)
      }
      return response
    },
    [logout, setError, processarMudancaUnidadeDeNegocio],
  )

  useLayoutEffect(() => {
    const requestInterceptor = api.interceptors.request.use((config) => {
      if (!navigator.onLine) {
        toastSemInternet()
        return Promise.reject(new Error('Sem conexão com a internet'))
      }

      if (user?.unidadeDeNegocioId) {
        config.headers['UnidadeDeNegocioId'] = user.unidadeDeNegocioId
      }

      return config
    })

    const responseInterceptor = api.interceptors.response.use(recuperarUnidadeDeNegocio, async (error) => {
      const originalRequest = error.config

      if (
        !error.response ||
        error.response.status !== HttpStatusCode.Unauthorized ||
        originalRequest.url.includes('/api/v2/Login/RenovarToken') ||
        originalRequest._retry
      ) {
        throw error
      }

      originalRequest._retry = true

      if (isRefreshing) {
        return handleTokenRefresh(originalRequest)
      }
      isRefreshing = true

      try {
        await handleRefreshToken()
        onRefreshed()
        return ventasApi(originalRequest)
      } catch (refreshError) {
        if (refreshError instanceof AxiosError) {
          setError({ descricao: refreshError?.response?.data?.descricao, titulo: 'Sessão expirada' })
        }
        await logout()
        throw refreshError
      } finally {
        isRefreshing = false
      }
    })

    setIsInicializado(true)

    return () => {
      api.interceptors.response.eject(responseInterceptor)
      api.interceptors.request.eject(requestInterceptor)
    }
  }, [recuperarUnidadeDeNegocio, setUser, user, api, handleRefreshToken, logout, setError])

  return isInicializado ? <>{children}</> : null
}

export { AxiosInterceptor }
